// src/utils/reviews/reviews-core.ts (NEW FILE)
import { ReviewsData } from '../ssr/types';

const STORE = 'milanlaser.com';
const MIN_REVIEWS = process.env.GATSBY_MIN_REVIEWS_COUNT ? parseInt(process.env.GATSBY_MIN_REVIEWS_COUNT, 10) : 5;

interface ReviewsApiResponse {
    review_count: number;
    average_rating: string | null;
}

interface BatchRatingResponse {
    sku: string;
    average_rating: string;
    num_ratings: number;
}

// Shared transformation logic
export function transformReviewsResponse(totalReviews: number, average: string | null): ReviewsData {
    return {
        total: totalReviews,
        average:
            totalReviews >= MIN_REVIEWS && average !== null
                ? (Math.round(parseFloat(average) * 10) / 10).toFixed(1)
                : null,
        bestRating: '5',
        worstRating: '1',
    };
}

// Single store fetch - shared implementation
export async function fetchReviewsForStore(storeSku: string): Promise<ReviewsData | null> {
    if (!storeSku || process.env.GATSBY_SHOW_REVIEWS_IO !== 'true') {
        return null;
    }

    try {
        const resp = await fetch(`https://api.reviews.io/reviews?store=${STORE}&sku=${storeSku}&type=product_review`);

        if (!resp.ok) {
            throw new Error(`Failed to fetch reviews: ${resp.status}`);
        }

        const json = (await resp.json()) as ReviewsApiResponse;
        return transformReviewsResponse(json.review_count, json.average_rating);
    } catch (error) {
        console.warn('Failed to fetch reviews:', storeSku, error);
        return null;
    }
}

// Batch fetch - shared implementation
export async function fetchBatchStoreReviews(storeSkus: string[]): Promise<Record<string, ReviewsData | null>> {
    if (!storeSkus.length || process.env.GATSBY_SHOW_REVIEWS_IO !== 'true') {
        return {};
    }

    try {
        const encodedSkus = storeSkus.map((sku) => encodeURIComponent(sku));
        const skuParam = encodedSkus.join(';');
        const url = `https://api.reviews.io/product/rating-batch?store=${STORE}&sku=${skuParam}`;

        const resp = await fetch(url);
        if (!resp.ok) {
            throw new Error(`Failed to fetch batch reviews: ${resp.status}`);
        }

        const batchResults = (await resp.json()) as BatchRatingResponse[];
        const results: Record<string, ReviewsData | null> = {};

        batchResults.forEach((result) => {
            results[result.sku] = transformReviewsResponse(result.num_ratings, result.average_rating);
        });

        // Ensure all requested SKUs have entries
        storeSkus.forEach((sku) => {
            if (!(sku in results)) {
                results[sku] = transformReviewsResponse(0, null);
            }
        });

        return results;
    } catch (error) {
        console.warn('Failed to fetch batch reviews:', error);
        return storeSkus.reduce((acc, sku) => ({ ...acc, [sku]: null }), {});
    }
}

// Server-specific: Chunked batch processing for large datasets
const MAX_BATCH_SIZE = 50;
const BATCH_DELAY_MS = 100;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function chunkArray<T>(array: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size));
    }
    return chunks;
}

// Server-side batch with chunking (handles 400+ stores)
export async function fetchReviewsForStoresBatch(storeSkus: string[]): Promise<Record<string, ReviewsData | null>> {
    if (!storeSkus.length) return {};

    const results: Record<string, ReviewsData | null> = {};
    const chunks = chunkArray(storeSkus, MAX_BATCH_SIZE);

    for (const [index, chunk] of chunks.entries()) {
        try {
            if (index > 0) await delay(BATCH_DELAY_MS);
            const batchResults = await fetchBatchStoreReviews(chunk);
            Object.assign(results, batchResults);
        } catch (error) {
            console.warn(`Chunk ${index + 1} failed:`, error);
            chunk.forEach((sku) => {
                if (!(sku in results)) results[sku] = null;
            });
        }
    }

    return results;
}

export { MIN_REVIEWS, STORE };
